import {useCallback, useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useAppDispatch} from "../../../../hooks/useAppDispatch";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {useSearchParams} from "react-router-dom";
import {toast} from "sonner";
import {Check, X} from "lucide-react";
import {Button} from "../../../basic/button";
import {PricingCard} from "./PricingCard.tsx";
import {
    detectCurrency,
    selectCurrency,
    selectCountry,
    selectCurrencyInitialized,
    selectCurrencyLoading
} from "../../../../store/currencySlice";
import {PricingDisclaimer} from "./PricingDisclaimer.tsx";
import {BillingPeriod} from "./types.ts";
import {
    useCreateCheckoutSessionMutation,
    useCreateInvoiceSubscriptionMutation,
    useCreateTrialSubscriptionMutation,
    useGetCurrentUserQuery,
    useGetPricingQuery,
    useUpdateProfileMutation
} from "../../../../store/generated/api.ts";
import {TrialDialog} from "../../../dialogs/user/TrialDialog";
import {InvoiceRequestDialog} from "../../../dialogs/user/InvoiceRequestDialog";
import {PaymentMethodDialog} from "../../../dialogs/user/PaymentMethodDialog";
import {selectHasPendingUpdates} from "../../../../store/updatesSlice";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "../../../basic/tabs.tsx";
import {isSupportedLanguage, mapBackendLanguageToI18nLocale} from "../../../../utils/localeUtils";
import {DialogType, useDialogContext} from "../../../../contexts/DialogContext";
import pricingProductCatalog from "./pricingProductCatalog.ts";
import type {PricingProductDefinition, PricingProductKey, PurchaseFlow} from "./pricingProductCatalog.ts";

const EDUCATION_PRODUCT_KEYS = ['education_student', 'education'] as const;
type CustomerType = "private" | "business";

const isEducationProduct = (productKey: string): boolean => EDUCATION_PRODUCT_KEYS.includes(productKey as any);

interface Product {
    key: PricingProductKey;
    label: string;
    title: string;
    description?: string;
    highlighted: boolean;
    trialCta: string;
    buyCta: string;
    featureKeys: string[];
    contactOnly?: boolean;
    showTrial?: boolean;
    purchaseFlow: PurchaseFlow;
    hideQuantity?: boolean;
}

export function PricingView() {
    const {t, i18n} = useTranslationWithKeys();
    const [searchParams] = useSearchParams();
    const dispatch = useAppDispatch();
    const hasPendingUpdates = useSelector(selectHasPendingUpdates);
    const billingPeriod: BillingPeriod = "yearly";
    const userCurrency = useSelector(selectCurrency);
    const userCountry = useSelector(selectCountry);
    const currencyLoading = useSelector(selectCurrencyLoading);
    const currencyInitialized = useSelector(selectCurrencyInitialized);
    const [userLocale, setUserLocale] = useState<string | undefined>();
    const {data: userData} = useGetCurrentUserQuery();
    const {
        data: pricingData,
        isLoading: pricingLoading,
        error: pricingError
    } = useGetPricingQuery({"Accept-Currency": userCurrency});
    const {openDialog, activeDialog, closeDialog, activeDialogData} = useDialogContext();

    const hasEducationLicense = pricingData?.products?.some(
        product => isEducationProduct(product.productKey) && product.hasValidLicense
    ) ?? false;
    const [processingProductKey, setProcessingProductKey] = useState<string | null>(null);
    const [quantities, setQuantities] = useState<Record<string, number>>({
        standard: 1,
        designer: 1,
        designer_alumni: 1,
        education_student: 1,
        education: 1
    });
    const [activeTab, setActiveTab] = useState<"energy" | "education">("energy");
    const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<{
        productName: string;
        productKey: string;
        quantity: number;
        purchasePrice: number;
        supportPrice: number;
        currency: string;
        pricingType: string;
        licenseDurationLabel?: string;
        packageSize?: number;
        requireVat?: boolean;
    } | null>(null);
    const queryParamsProcessedRef = useRef(false);
    const languageProcessedRef = useRef(false);
    const [discountCode, setDiscountCode] = useState<string>('');
    const [discountCodeApplied, setDiscountCodeApplied] = useState(false);

    const [createCheckoutSession] = useCreateCheckoutSessionMutation();
    const [createTrialSubscription] = useCreateTrialSubscriptionMutation();
    const [createInvoiceSubscription] = useCreateInvoiceSubscriptionMutation();
    const [updateProfile] = useUpdateProfileMutation();

    useEffect(() => {
        if (!currencyInitialized && !currencyLoading) {
            dispatch(detectCurrency());
        }
    }, [dispatch, currencyInitialized, currencyLoading]);

    useEffect(() => {
        const language = userData?.user?.language;
        setUserLocale(language);
    }, [userData]);

    const getProductName = (productKey: string | undefined): string => {
        if (!productKey) return '';
        const key = productKey === 'education'
            ? 'educational'
            : productKey === 'education_student'
                ? 'educationalStudent'
                : productKey === 'designer_alumni'
                    ? 'designerAlumni'
                    : productKey;
        return t(`pricing.products.${key}.label`);
    };

    const availablePricingProductKeys = new Set((pricingData?.products ?? []).map(product => product.productKey));

    const handleQuantityChange = (productKey: string, quantity: number) => {
        setQuantities(prev => ({
            ...prev,
            [productKey]: quantity
        }));
    };

    const handleTrialClick = (product: Product) => {
        if (processingProductKey || hasPendingUpdates) {
            if (hasPendingUpdates) {
                toast.info(t('pricing.pendingUpdates'));
            }
            return;
        }
        openDialog(DialogType.TRIAL, {productKey: product.key});
    };

    const handleTrialSubmit = async (productKey: string, quantity: number) => {
        if (processingProductKey) return;

        setProcessingProductKey(productKey);

        try {
            await createTrialSubscription({
                "Detected-Country": userCountry || undefined,
                vwpCreateTrialSubscriptionRequest: {
                    productKey: productKey as "standard" | "designer" | "designer_alumni" | "education_student" | "education",
                    quantity,
                    discountCode: discountCode || undefined
                }
            }).unwrap();

            toast.success(t('trial.success'));

            const successUrl = `${window.location.origin}/dashboard?payment=success`;
            window.location.href = successUrl;
        } catch (error) {
            if (error?.data?.error === 'INVALID_DISCOUNT_CODE') {
                toast.error(t('pricing.invalidDiscountCode'));
                setDiscountCodeApplied(false);
            } else {
                toast.error(t('trial.error'));
            }
        } finally {
            setProcessingProductKey(null);
        }
    };

    const handleBuyClick = async (product: Product) => {
        if (processingProductKey || hasPendingUpdates) {
            if (hasPendingUpdates) {
                toast.info(t('pricing.pendingUpdates'));
            }
            return;
        }

        if (product.purchaseFlow === "card") {
            await handleCardPayment(product);
        } else {
            openDialog(DialogType.PURCHASE, {productKey: product.key});
        }
    };

    const handleCardPayment = useCallback(async (product?: Product, customerType: CustomerType = "private", vatNumber?: string) => {
        const targetProduct = product || (activeDialogData?.productKey ? {key: activeDialogData.productKey} as Product : null);
        if (!targetProduct || processingProductKey) return;

        setProcessingProductKey(targetProduct.key);

        try {
            const successUrl = `${window.location.origin}/dashboard?payment=success`;
            const cancelUrl = `${window.location.origin}/pricing?payment=cancel`;

            const response = await createCheckoutSession({
                vwpCreateCheckoutSessionRequest: {
                    productKey: targetProduct.key as "standard" | "designer" | "designer_alumni" | "education_student" | "education",
                    successUrl,
                    cancelUrl,
                    trial: false,
                    quantity: quantities[targetProduct.key],
                    discountCode: discountCode || undefined,
                    customerType,
                    vatNumber: customerType === "business" ? vatNumber : undefined
                }
            }).unwrap();

            if (response.sessionUrl) {
                window.location.href = response.sessionUrl;
            }
        } catch (error) {
            if (error?.data?.error === 'INVALID_DISCOUNT_CODE') {
                toast.error(t('pricing.invalidDiscountCode'));
                setDiscountCodeApplied(false);
            } else {
                const errorMessage = t('pricing.checkoutError');
                toast.error(errorMessage);
            }
        } finally {
            setProcessingProductKey(null);
        }
    }, [activeDialogData, processingProductKey, createCheckoutSession, quantities, t]);

    const getProductTranslationKey = (productKey: PricingProductKey): string => {
        if (productKey === 'education') return 'educational';
        if (productKey === 'education_student') return 'educationalStudent';
        if (productKey === 'designer_alumni') return 'designerAlumni';
        return productKey;
    };

    const toPricingProduct = (definition: PricingProductDefinition): Product => {
        const translationKey = getProductTranslationKey(definition.key);
        return {
            key: definition.key,
            label: t(`pricing.products.${translationKey}.label`),
            title: t(`pricing.products.${translationKey}.title`),
            description: t(`pricing.products.${translationKey}.description`),
            highlighted: definition.highlighted,
            trialCta: t('pricing.buttons.startFreeTrial'),
            buyCta: t('pricing.buttons.buyNow'),
            featureKeys: definition.featureKeys,
            contactOnly: definition.contactOnly,
            showTrial: definition.showTrial,
            purchaseFlow: definition.purchaseFlow ?? "customer-type",
            hideQuantity: definition.hideQuantity
        };
    };

    const energySystemsProducts: Product[] = (pricingProductCatalog.energySystemsProductCatalog ?? [])
        .filter(product => !product.hidden)
        .map(toPricingProduct);
    const educationProducts: Product[] = (pricingProductCatalog.educationProductCatalog ?? [])
        .filter(product => !product.hidden)
        .filter(product => product.key !== 'designer_alumni' || availablePricingProductKeys.has('designer_alumni'))
        .map(toPricingProduct);

    const getPurchaseFlow = (productKey?: string): PurchaseFlow =>
        [...energySystemsProducts, ...educationProducts].find(p => p.key === productKey)?.purchaseFlow ?? "customer-type";

    // Handle query parameters for external links
    useEffect(() => {
        const product = searchParams.get('product');
        const intent = searchParams.get('intent');
        const lang = searchParams.get('lang');
        const quantityParam = searchParams.get('quantity');

        if (!product || !intent) return;

        if (queryParamsProcessedRef.current) return;

        // Handle language parameter
        if (lang && isSupportedLanguage(lang) && !languageProcessedRef.current) {
            const i18nLocale = mapBackendLanguageToI18nLocale(lang);
            if (i18n.language !== i18nLocale) {
                i18n.changeLanguage(i18nLocale);

                // Persist language to backend
                updateProfile({
                    vwpAuthUserUpdateRequest: {language: lang}
                }).catch(error => {
                    console.error('Failed to persist language preference:', error);
                });
            }
            languageProcessedRef.current = true;
        }

        // Wait for pricing data to load
        if (!pricingData || pricingLoading) return;

        // Find product based on query parameter
        let targetProduct: Product | null = null;

        if (product === 'standard' || product === 'designer' || product === 'api') {
            targetProduct = energySystemsProducts.find(p => p.key === product) || null;
            setActiveTab('energy');
        } else if (product === 'education_student' || product === 'education' || product === 'designer_alumni') {
            targetProduct = educationProducts.find(p => p.key === product) || null;
            setActiveTab('education');
        }

        if (!targetProduct) return;

        // Handle quantity parameter (ignore for trial and student)
        const shouldApplyQuantity = intent !== 'trial' && product !== 'education_student';
        if (shouldApplyQuantity && quantityParam) {
            const parsedQuantity = parseInt(quantityParam, 10);
            if (!isNaN(parsedQuantity) && parsedQuantity > 0) {
                setQuantities(prev => ({
                    ...prev,
                    [product]: parsedQuantity
                }));
            }
        }

        // Handle intent
        if (intent === 'trial' && targetProduct.showTrial !== false) {
            if (activeDialog === DialogType.TRIAL || activeDialog === DialogType.PURCHASE) {
                return;
            }
            queryParamsProcessedRef.current = true;
            openDialog(DialogType.TRIAL, {productKey: targetProduct.key});
        } else if (intent === 'purchase') {
            if (activeDialog === DialogType.TRIAL || activeDialog === DialogType.PURCHASE) {
                return;
            }
            queryParamsProcessedRef.current = true;
            if (targetProduct.purchaseFlow === "card") {
                // For products with a direct card flow (e.g. student/alumni), go straight to card payment
                setTimeout(() => handleCardPayment(targetProduct), 100);
            } else {
                // For other products, show payment method selection
                openDialog(DialogType.PURCHASE, {productKey: targetProduct.key});
            }
        }

        // Clean up URL parameters after processing
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
    }, [pricingData, pricingLoading, handleCardPayment]);

    const handleInvoicePayment = () => {
        const productKey = activeDialogData?.productKey;
        if (!productKey) return;

        const pricing = pricingData?.products?.find(p => p.productKey === productKey);
        if (!pricing) return;

        const calculateTieredPrice = (price: any, qty: number): number => {
            if (!price) return 0;
            if (price.tiers && price.tiers.length > 0) {
                const sortedTiers = [...price.tiers].sort((a, b) => (a.upTo || Infinity) - (b.upTo || Infinity));
                const tier = sortedTiers.find(t => !t.upTo || qty <= t.upTo);
                if (tier) {
                    return (tier.unitAmount || 0) * qty + (tier.flatAmount || 0);
                }
            }
            return (price.amount || 0) * qty;
        };

        const purchasePrice = calculateTieredPrice(pricing.purchasePrice, quantities[productKey]);
        const supportPrice = calculateTieredPrice(pricing.supportPrice, quantities[productKey]);

        setSelectedProduct({
            productName: getProductName(productKey),
            productKey: productKey,
            quantity: quantities[productKey],
            purchasePrice,
            supportPrice,
            currency: pricing.purchasePrice?.currency || userCurrency,
            pricingType: pricing.pricingType || '',
            licenseDurationLabel: pricing.licenseDurationLabel,
            packageSize: pricing.packageSize,
            requireVat: true
        });

        setInvoiceDialogOpen(true);
    };

    const handleInvoiceSubmit = async (_productKey: string, quantity: number, vatNumber?: string) => {
        if (!selectedProduct || processingProductKey || hasPendingUpdates) {
            if (hasPendingUpdates) {
                toast.info(t('pricing.pendingUpdates'));
            }
            return;
        }

        setProcessingProductKey(selectedProduct.productKey);

        try {
            const response = await createInvoiceSubscription({
                "Detected-Country": userCountry || undefined,
                vwpCreateInvoiceSubscriptionRequest: {
                    productKey: selectedProduct.productKey as "standard" | "designer",
                    quantity,
                    discountCode: discountCode || undefined,
                    customerType: "business",
                    vatNumber: vatNumber?.trim() || undefined
                }
            }).unwrap();

            toast.success(t('invoice.subscriptionCreated'));

            const successUrl = `${window.location.origin}/dashboard?payment=success`;
            window.location.href = successUrl;
        } catch (error) {
            if (error?.data?.error === 'INVALID_DISCOUNT_CODE') {
                toast.error(t('pricing.invalidDiscountCode'));
                setDiscountCodeApplied(false);
            } else {
                const errorMessage = t('pricing.checkoutError');
                toast.error(errorMessage);
            }
        } finally {
            setProcessingProductKey(null);
        }
    };

    const handleContactClick = () => {
        window.location.href = "mailto:sales@velasolaris.com";
    };

    const handleApplyDiscountCode = () => {
        if (discountCode.trim()) {
            setDiscountCodeApplied(true);
            toast.success(t('pricing.discountCodeApplied'));
        }
    };

    const handleClearDiscountCode = () => {
        setDiscountCode('');
        setDiscountCodeApplied(false);
        toast.success(t('pricing.discountCodeCleared'));
    };

    if (currencyLoading) {
        return (
            <div className="space-y-6">
                <div className="text-center max-w-3xl mx-auto">
                    <h1>{t('pricing.title')}</h1>
                    <p className="text-muted-foreground mt-2">{t('pricing.subtitle')}</p>
                </div>
                <div className="text-center py-12">
                    <div className="animate-pulse bg-muted h-96 rounded-lg max-w-3xl mx-auto"/>
                </div>
            </div>
        );
    }

    if (pricingError) {
        return (
            <div className="space-y-6">
                <div className="text-center max-w-3xl mx-auto">
                    <h1>{t('pricing.title')}</h1>
                    <p className="text-muted-foreground mt-2">{t('pricing.subtitle')}</p>
                </div>
                <div className="text-center py-12">
                    <p className="text-destructive">{t('pricing.loadError')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="text-center max-w-3xl mx-auto">
                <h1>{t('pricing.title')}</h1>
                <p className="text-muted-foreground mt-2">{t('pricing.subtitle')}</p>
            </div>

            <Tabs defaultValue="energy" value={activeTab}
                  onValueChange={(value) => setActiveTab(value as "energy" | "education")}>
                <TabsList className="flex gap-4 mx-auto">
                    <TabsTrigger
                        value="energy"
                        className="px-6 py-3 rounded-full text-base font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                    >
                        {t('pricing.tabs.energySystems')}
                    </TabsTrigger>
                    <TabsTrigger
                        value="education"
                        className="px-6 py-3 rounded-full text-base font-medium data-[state=active]:bg-primary data-[state=active]:text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                    >
                        {t('pricing.tabs.education')}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="energy" className="mt-6">
                    {pricingLoading ? (
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-96 animate-pulse bg-muted rounded-lg"/>
                            ))}
                        </div>
                    ) : (
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {energySystemsProducts.map((product) => (
                                <PricingCard
                                    key={product.key}
                                    product={product}
                                    billingPeriod={billingPeriod}
                                    pricingData={pricingData?.products?.find(p => p.productKey === product.key)}
                                    onTrialClick={handleTrialClick}
                                    onBuyClick={handleBuyClick}
                                    onContactClick={handleContactClick}
                                    isLoading={processingProductKey === product.key}
                                    quantity={quantities[product.key]}
                                    onQuantityChange={(quantity) => handleQuantityChange(product.key, quantity)}
                                    userCurrency={userCurrency}
                                    userLocale={userLocale}
                                    hasEducationLicense={hasEducationLicense}
                                />
                            ))}
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="education" className="mt-6">
                    {pricingLoading ? (
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-96 animate-pulse bg-muted rounded-lg"/>
                            ))}
                        </div>
                    ) : (
                        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                            {educationProducts.map((product) => (
                                <PricingCard
                                    key={product.key}
                                    product={product}
                                    billingPeriod={billingPeriod}
                                    pricingData={pricingData?.products?.find(p => p.productKey === product.key)}
                                    onTrialClick={handleTrialClick}
                                    onBuyClick={handleBuyClick}
                                    onContactClick={handleContactClick}
                                    isLoading={processingProductKey === product.key}
                                    quantity={quantities[product.key]}
                                    onQuantityChange={(quantity) => handleQuantityChange(product.key, quantity)}
                                    userCurrency={userCurrency}
                                    userLocale={userLocale}
                                    hasEducationLicense={hasEducationLicense}
                                />
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            <div className="max-w-md mx-auto">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <input
                            type="text"
                            value={discountCode}
                            onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                            placeholder={t('pricing.discountCodePlaceholder')}
                            disabled={discountCodeApplied}
                            className="w-full px-4 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
                        />
                        {discountCodeApplied && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600">
                                <Check className="h-4 w-4"/>
                            </div>
                        )}
                    </div>
                    {discountCodeApplied ? (
                        <Button
                            onClick={handleClearDiscountCode}
                            variant="secondary"
                            className="flex items-center gap-2"
                        >
                            <X className="h-4 w-4"/>
                            {t('pricing.clear')}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleApplyDiscountCode}
                            disabled={!discountCode.trim()}
                        >
                            {t('pricing.apply')}
                        </Button>
                    )}
                </div>
                {discountCodeApplied && (
                    <p className="text-sm text-green-600 mt-2">
                        {t('pricing.discountCodeAppliedMessage')}
                    </p>
                )}
            </div>

            <PricingDisclaimer/>

            {activeDialog === DialogType.TRIAL && (
                <TrialDialog
                    open={true}
                    onOpenChange={(open) => !open && closeDialog(DialogType.TRIAL)}
                    productKey={activeDialogData?.productKey}
                    defaultQuantity={3}
                    onSubmit={handleTrialSubmit}
                />
            )}

            {selectedProduct && (
                <InvoiceRequestDialog
                    open={invoiceDialogOpen}
                    onOpenChange={setInvoiceDialogOpen}
                    onSubmit={handleInvoiceSubmit}
                    productInfo={selectedProduct}
                    title={t('pricing.invoiceRequestTitle')}
                    description={t('pricing.invoiceRequestDescription')}
                    requireVat={selectedProduct.requireVat}
                />
            )}

            {activeDialog === DialogType.PURCHASE && (
                <PaymentMethodDialog
                    open={true}
                    onOpenChange={(open) => !open && closeDialog(DialogType.PURCHASE)}
                    onCardPayment={(customerType, vatNumber) => handleCardPayment(undefined, customerType, vatNumber)}
                    onInvoicePayment={handleInvoicePayment}
                    title={activeDialogData?.productKey ? t('trial.purchaseProduct', {productName: getProductName(activeDialogData.productKey)}) : undefined}
                    flow={getPurchaseFlow(activeDialogData?.productKey)}
                />
            )}
        </div>
    );
}
